import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { FieldConfigDTO, FilterConfigDTO, MaintainTabDTO, MenuDTO, MenuType } from '../../../../../system/system-dtos';
import { MyErrorStateMatcher, ValidatorConfig } from '../../../../../common/field-validation/my-error-state-matcher';
import { UiValidator } from '../../../../../common/field-validation/ui-validator.dto';
import { DataSource } from '../../data-source/data-source';
import { QueryDataSource } from '../../data-source/query-data-source';
import { EntityGroupDataSource } from '../../data-source/entity-group-data-source';
import { MaintainService } from '../../maintain.service';
import { QuickFilterDTO } from '../../../../data-type-widgets/data-type/lottomart/model/lottomart-model';
import { MenuUtils } from '../../../../../common/utils/menu-utils';

@Component({
  selector: 'app-maintain-filters',
  templateUrl: './maintain-filters.component.html',
  styleUrls: ['./maintain-filters.component.scss'],
  standalone: false,
})
export class MaintainFiltersComponent implements OnChanges, OnDestroy {
  @Output() filterEmitter = new EventEmitter<{ [key: string]: string }>();

  @Input() currentTab: MaintainTabDTO;

  @Input() menuConfig: MenuDTO;

  @Input() mode: number;

  @Input() dataSource: DataSource;

  @Input() filterOn = false;

  filters: FieldConfigDTO[] = [];

  formGroup: UntypedFormGroup;

  tableViewId: string;

  matcher = new MyErrorStateMatcher();

  elasticFilter = false;

  elasticFilterGroup;

  elasticFilters: QuickFilterDTO[];

  public static isElastic(dataSource: DataSource, mode: number, menuConfig: MenuDTO) {
    return (
      (dataSource instanceof EntityGroupDataSource &&
        menuConfig.entityGroup.dataSources[mode].type == MenuType.ELASTIC) ||
      dataSource instanceof QueryDataSource
    );
  }

  public static isCassandra(dataSource: DataSource, mode: number, menuConfig: MenuDTO) {
    return (
      (dataSource instanceof EntityGroupDataSource &&
        menuConfig.entityGroup.dataSources[mode].type == MenuType.CASSANDRA) ||
      dataSource instanceof QueryDataSource
    );
  }

  constructor(private maintainService: MaintainService) {}

  ngOnInit(): void {}

  private initializeForm() {
    this.tableViewId = this.determineTableViewId();
    if (!this.currentTab.tableView.filters) {
      this.elasticFilter = false;
      this.elasticFilters = [];
      this.filterOn = false;
      this.formGroup = null;
      return;
    }
    this.elasticFilter = false;
    this.elasticFilters = [];
    this.filters = this.currentTab.tableView.filters
      .filter((f) => this.isFilterApplicableForVIew(f))
      .filter((f) => {
        const normal = f.name !== 'ELASTIC_FILTER';
        if (!normal) {
          this.elasticFilter = true;
        }
        return normal;
      });

    const group: any = {};
    this.currentTab.tableView.filters.forEach((filter) => {
      // group[filter.name] = new FormControl(filter.value, MyErrorStateMatcher.getValidators(filter.validators));
      group[filter.name] = new UntypedFormControl(filter.value);
    });

    this.formGroup = new UntypedFormGroup(group);
    if (this.elasticFilter) {
      this.elasticFilterGroup = MenuUtils.getNthConfigAsElastic(
        this.menuConfig.entityGroup,
        this.menuConfig.entityGroup.currentMode || 0
      ).elasticFilterGroup;
      this.maintainService.getElasticFilterForGroup(this.elasticFilterGroup).subscribe((d) => {
        d.forEach((f) => {
          f.ngStyle = f.ngStyle ? JSON.parse(f.ngStyle) : undefined;
        });
        this.elasticFilters = d;
      });
    }
  }

  private isFilterApplicableForVIew(f) {
    return f.filterApplyFor && f.filterApplyFor.length > 0 && this.tableViewId
      ? f.filterApplyFor.includes(this.tableViewId)
      : true;
  }

  reset() {
    if (this.formGroup) {
      this.formGroup.reset();
    }
  }

  setCurrentValues() {
    this.currentTab.tableView.filters.forEach((f) => {
      const { value } = this.formGroup.get(f.name);
      f.value = value || '';
    });
  }

  submit() {
    this.setCurrentValues();
    const filtersValue: { [key: string]: string } = {};
    this.currentTab.tableView.filters.forEach((filter) => {
      filtersValue[filter.name] = filter.value;
    });
    this.filterEmitter.emit(filtersValue);
  }

  condition(v: UiValidator, filter: FilterConfigDTO) {
    const formControl = this.formGroup.get(filter.name);
    return this.getValidatorConfig(v).condition(formControl);
  }

  message(v: UiValidator) {
    return this.getValidatorConfig(v).message(v);
  }

  private getValidatorConfig(v: UiValidator): ValidatorConfig {
    return MyErrorStateMatcher.messageMap[v.type];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.keys(changes).length === 1 && changes.filterOn) {
      return;
    }
    this.initializeForm();
  }

  private determineTableViewId() {
    if (!this.menuConfig.entityGroup) {
      return null;
    }

    return this.menuConfig.entityGroup.dataSources[this.mode].definition.id;
  }

  ngOnDestroy(): void {
    this.filters.forEach((filter) => (filter.value = null));
  }

  elasticFilterClicked(filterDef: QuickFilterDTO) {
    let { value } = this.formGroup.get('ELASTIC_FILTER');
    if (!value || value.length === 0) {
      value = '';
    } else {
      value += ' AND ';
    }
    this.formGroup.get('ELASTIC_FILTER').setValue(value + filterDef.queryString);
  }

  elasticFilterDoubleClicked(filterDef: QuickFilterDTO) {
    this.formGroup.get('ELASTIC_FILTER').setValue(filterDef.queryString);
    this.submit();
  }
}
